import { map } from 'lodash';

import * as api from '../services/API';
import { actions } from '../ducks/Milestones';
import onError from './Error';
import Immutable from 'seamless-immutable';
import { sortAuthorities } from '../utils/SortUtil';
import { Comment } from '@/models/CommentModel';
import { Dispatch } from 'redux';
import { Milestone } from '@/models/MilestoneModel';
import { Field } from '@/models/card/CardModel';

export const fetchMilestones = ({
  communityId,
  roomId,
  boardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchMilestonesRequest({ boardId }));

    try {
      const milestones = await api.fetchMilestones({ communityId, roomId, boardId });

      return dispatch(actions.fetchMilestonesSuccess({ milestones }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchMilestonesError({ error }) });
    }
  };
};

export const createMilestone = ({
  communityId,
  roomId,
  boardId,
  containerId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  containerId: string;
  data: Field[];
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.createMilestoneRequest());

    try {
      const milestone = await api.createMilestone({ communityId, roomId, boardId, containerId, data });

      return dispatch(actions.createMilestoneSuccess({ milestone, milestoneSet: containerId }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.createMilestoneError({ error }) });
    }
  };
};

export const updateMilestone = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  milestoneSet,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  milestoneSet: string;
  data: Field[];
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.updateMilestoneRequest());

    try {
      const milestone = await api.updateMilestone({ communityId, roomId, boardId, milestoneId, data });

      return dispatch(actions.updateMilestoneSuccess({ milestone, milestoneSet }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateMilestoneError({ error }) });
    }
  };
};

export const updateMilestoneImmediately = ({
  communityId,
  roomId,
  boardId,
  milestone,
  milestoneSet,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestone: Milestone;
  milestoneSet: string;
  data: Field[];
}) => {
  return async (dispatch: Dispatch) => {
    const updatedMilestone = milestone.merge({
      fields: milestone.fields.map((field) => {
        const fields = data.filter((updatedField) => updatedField.id === field.id);
        return fields.length ? Immutable(fields[0]) : field;
      }),
      fakeModel: true,
    });

    dispatch(actions.updateMilestoneImmediatelySuccess({ milestone: updatedMilestone, milestoneSet }));

    try {
      const cardFromBackend = await api.updateMilestone({
        communityId,
        roomId,
        boardId,
        milestoneId: milestone.milestoneId,
        data: { type: 'entry', fields: data },
      });

      return dispatch(actions.updateMilestoneSuccess({ milestone: cardFromBackend, milestoneSet }));
    } catch (error) {
      return onError({
        error,
        dispatch,
        action: actions.updateMilestoneImmediatelyError({ error, milestone, milestoneSet }),
      });
    }
  };
};

export const deleteMilestone = ({
  communityId,
  roomId,
  boardId,
  milestone,
  milestoneSet,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestone: Milestone;
  milestoneSet: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.deleteMilestoneRequest());

    try {
      await api.deleteMilestone({ communityId, roomId, boardId, milestoneId: milestone.id });

      return dispatch(actions.deleteMilestoneSuccess({ boardId, milestone, milestoneSet }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.deleteMilestoneError({ error }) });
    }
  };
};

export const fetchAttachments = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchAttachmentsRequest());

    try {
      const attachments = await api.fetchMilestoneAttachments({ communityId, roomId, boardId, milestoneId });

      return dispatch(actions.fetchAttachmentsSuccess({ attachments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchAttachmentsError({ error }) });
    }
  };
};

export const uploadAttachment = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  files,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  files: File[];
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.uploadAttachmentRequest());
    try {
      const uploads = [];
      const attachUpload = (file: File) =>
        uploads.push(api.uploadMilestoneAttachment({ communityId, roomId, boardId, milestoneId, file }));
      map(files, attachUpload);
      const attachments = await Promise.all(uploads);

      return dispatch(actions.uploadAttachmentSuccess({ milestoneId, attachments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.uploadAttachmentError({ error }) });
    }
  };
};

export const addMilestoneLink = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  link,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  link: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.addLinkRequest());

    try {
      const createdLink = await api.addMilestoneLink({ communityId, roomId, boardId, milestoneId, link });

      return dispatch(actions.addLinkSuccess({ milestoneId, link: createdLink }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.addLinkError({ error }) });
    }
  };
};

export const deleteAttachment = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  attachmentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  attachmentId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.deleteAttachmentsRequest());

    try {
      await api.deleteMilestoneAttachment({ communityId, roomId, boardId, milestoneId, attachmentId });

      return dispatch(actions.deleteAttachmentsSuccess({ milestoneId, attachmentId }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.deleteAttachmentsError({ error }) });
    }
  };
};

export const createComment = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  content,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  content: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.createCommentRequest());
    try {
      const comment = await api.createMilestoneComment({ communityId, roomId, boardId, milestoneId, content });
      comment.cardId = milestoneId;

      return dispatch(actions.createCommentSuccess({ comment }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.createCommentError({ error }) });
    }
  };
};

export const updateComment = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  comment,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  comment: Comment;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.updateCommentRequest());

    try {
      const updatedComment = await api.updateMilestoneComment({ communityId, roomId, boardId, milestoneId, comment });
      updatedComment.cardId = milestoneId;

      return dispatch(actions.updateCommentSuccess({ comment: updatedComment }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCommentError({ error }) });
    }
  };
};

export const deleteComment = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
  commentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
  commentId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.deleteCommentRequest());

    try {
      await api.deleteMilestoneComment({ communityId, roomId, boardId, milestoneId, commentId });

      return dispatch(actions.deleteCommentSuccess({ commentId: commentId }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.deleteCommentError({ error }) });
    }
  };
};

export const fetchComments = ({
  communityId,
  roomId,
  boardId,
  milestoneId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  milestoneId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchCommentsRequest());

    try {
      const comments = await api.fetchMilestoneComments({ communityId, roomId, boardId, milestoneId });
      comments.forEach(function (comment: Comment) {
        comment.cardId = milestoneId;
      });

      return dispatch(actions.fetchCommentsSuccess({ comments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchCommentsError({ error }) });
    }
  };
};

export const fetchLog = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchLogRequest());

    try {
      const log = await api.fetchCardLog({ communityId, roomId, boardId, cardId });

      return dispatch(actions.fetchLogSuccess({ log }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchLogError({ error }) });
    }
  };
};

export const fetchPossibleResponsible = ({
  communityId,
  roomId,
  boardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchPossibleResponsibleRequest({ boardId }));

    try {
      // todo: implement a new API to get users and groups who have access to the folder,
      //  but decided to use all room members (users and groups) as a quick fix
      const allRoomMembers = await api.getAllRoomMembers({ communityId, roomId, boardId });
      const possibleResponsible = sortAuthorities(allRoomMembers);

      return dispatch(actions.fetchPossibleResponsibleSuccess({ possibleResponsible }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchPossibleResponsibleError({ error }) });
    }
  };
};
